/**
 * Denormalized query-friendly form of `Creation.itemsUsed[]`.
 *
 * Firestore's `array-contains` operator works on arrays of PRIMITIVES
 * (strings, numbers), never on arrays of objects. Creation.itemsUsed is
 * an array of objects (`{itemId, itemType, name, brand}`), so we can't
 * directly query "creations that used this polish". The workaround is
 * to keep a parallel string-only array — `itemsUsedRefs` — where each
 * entry encodes the same reference in a single string.
 *
 * Ref format: `{itemType}__{brandSlug}__{nameSlug}`
 *
 * Example:
 *   itemsUsed:      [{ itemType: 'polish', brand: 'Mooncat',
 *                      name: 'House of Hades', itemId: 'abc123' }]
 *   itemsUsedRefs:  ['polish__mooncat__house-of-hades']
 *
 * The detail-page query is then:
 *   collectionGroup('creations')
 *     .where('itemsUsedRefs', 'array-contains',
 *            'polish__mooncat__house-of-hades')
 *
 * Compute + write this on every creation add/edit so the two fields
 * never drift. A one-time backfill script populates it on legacy
 * creations that predate this feature.
 */
import { slugify } from './polishSlug';

export interface ItemUsedLike {
  /** Optional so callers can pass their own cleaned/optional-typed
   *  itemsUsed shape without a cast; empty/missing values are filtered
   *  in `itemsUsedRefs()` below. */
  itemType?: string;
  brand?: string;
  name?: string;
}

export function itemUsedRef(entry: ItemUsedLike): string {
  const brandSlug = slugify(entry.brand ?? '');
  const nameSlug = slugify(entry.name ?? '');
  return `${entry.itemType}__${brandSlug}__${nameSlug}`;
}

export function itemsUsedRefs(entries: ItemUsedLike[]): string[] {
  // De-dupe — a mani that used the same polish twice shouldn't produce
  // two identical refs; the detail-page section should count it once.
  const seen = new Set<string>();
  for (const e of entries) {
    if (!e.itemType || !e.brand || !e.name) continue;
    seen.add(itemUsedRef(e));
  }
  return Array.from(seen);
}
